import { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import API from '../../utils/api';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Badge from '../../components/common/Badge';
import { HiOutlineCheck, HiOutlineX } from 'react-icons/hi';

const TABS = [
  { v: 'pending',  label: 'বকেয়া', icon: '⏳' },
  { v: 'approved', label: 'অনুমোদিত', icon: '✓' },
  { v: 'rejected', label: 'প্রত্যাখ্যাত', icon: '✕' },
];

const STATUS_STYLES = {
  approved: { variant: 'success', label: 'অনুমোদিত' },
  rejected: { variant: 'danger',  label: 'প্রত্যাখ্যাত' },
  pending:  { variant: 'warning', label: 'বকেয়া' },
};

const Advances = () => {
  const [advances, setAdvances] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const { t } = useLanguage();

  useEffect(() => { fetchAdvances(); }, [filter]);

  const fetchAdvances = async () => {
    try { const { data } = await API.get('/admin/advances', { params: { status: filter } }); setAdvances(data.advances); }
    catch (error) { console.error('Error:', error); }
    finally { setLoading(false); }
  };

  const handleApprove = async (id) => {
    if (!confirm('অনুমোদন করবেন?')) return;
    try { await API.put(`/admin/advances/${id}/approve`); fetchAdvances(); }
    catch (error) { alert(error.response?.data?.message || 'Error'); }
  };

  const handleReject = async (id) => {
    if (!confirm('প্রত্যাখ্যান করবেন?')) return;
    try { await API.put(`/admin/advances/${id}/reject`); fetchAdvances(); }
    catch (error) { alert(error.response?.data?.message || 'Error'); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <PageHeader title={t('advances')} subtitle={`${advances.length} টি ${filter} অনুরোধ`} />

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto -mx-1 px-1">
        {TABS.map(({ v, label, icon }) => (
          <button
            key={v}
            onClick={() => setFilter(v)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold whitespace-nowrap transition-all ${
              filter === v
                ? 'bg-brand-600 text-white shadow-pop'
                : 'bg-white dark:bg-ink-800 border border-ink-200 dark:border-ink-700 text-ink-600 dark:text-ink-300 hover:bg-ink-50 dark:hover:bg-ink-700/60'
            }`}
          >
            <span>{icon}</span>
            {label}
          </button>
        ))}
      </div>

      {advances.length === 0 ? (
        <div className="card">
          <EmptyState
            icon="💸"
            title={filter === 'pending' ? 'কোনো বকেয়া অনুরোধ নেই' : `কোনো ${filter} অনুরোধ নেই`}
            description={filter === 'pending' ? 'সব অনুরোধ প্রক্রিয়া করা হয়েছে!' : undefined}
          />
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden md:block card p-0 overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-ink-50 dark:bg-ink-900/30 border-b border-ink-100 dark:border-ink-700">
                <tr>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">তারিখ</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">ওয়ার্কার</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">পরিমাণ</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">কারণ</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400">অবস্থা</th>
                  <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wider text-ink-500 dark:text-ink-400 text-right">অ্যাকশন</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100 dark:divide-ink-700">
                {advances.map((adv) => {
                  const style = STATUS_STYLES[adv.status] || STATUS_STYLES.pending;
                  return (
                    <tr key={adv.id} className="hover:bg-ink-50 dark:hover:bg-ink-900/30 transition-colors">
                      <td className="px-5 py-3 text-sm text-ink-700 dark:text-ink-300 whitespace-nowrap">
                        {new Date(adv.created_at).toLocaleDateString('en-GB')}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold text-xs">
                            {adv.worker?.name?.charAt(0)}
                          </div>
                          <span className="font-semibold text-ink-900 dark:text-white">{adv.worker?.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-base font-bold text-violet-700 dark:text-violet-300 whitespace-nowrap">৳{Number(adv.amount).toLocaleString('en-IN')}</td>
                      <td className="px-5 py-3 text-sm text-ink-600 dark:text-ink-400 max-w-xs truncate">{adv.reason || '—'}</td>
                      <td className="px-5 py-3">
                        <Badge variant={style.variant}>{style.label}</Badge>
                      </td>
                      <td className="px-5 py-3 text-right">
                        {adv.status === 'pending' && (
                          <div className="flex gap-1 justify-end">
                            <button onClick={() => handleApprove(adv.id)} className="p-2 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/40 rounded-lg transition-colors" title="অনুমোদন">
                              <HiOutlineCheck className="w-5 h-5" />
                            </button>
                            <button onClick={() => handleReject(adv.id)} className="p-2 text-rose-600 dark:text-rose-400 hover:bg-rose-100 dark:hover:bg-rose-900/40 rounded-lg transition-colors" title="প্রত্যাখ্যান">
                              <HiOutlineX className="w-5 h-5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {advances.map((adv) => {
              const style = STATUS_STYLES[adv.status] || STATUS_STYLES.pending;
              return (
                <div key={adv.id} className="card">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white font-bold flex-shrink-0">
                        {adv.worker?.name?.charAt(0)}
                      </div>
                      <div className="min-w-0">
                        <h3 className="font-semibold text-ink-900 dark:text-white truncate">{adv.worker?.name}</h3>
                        <p className="text-xs text-ink-500 dark:text-ink-400">{new Date(adv.created_at).toLocaleDateString('en-GB')}</p>
                      </div>
                    </div>
                    <Badge variant={style.variant}>{style.label}</Badge>
                  </div>
                  <div className="flex items-center justify-between pt-3 border-t border-ink-100 dark:border-ink-700">
                    <span className="text-base font-bold text-violet-700 dark:text-violet-300">৳{Number(adv.amount).toLocaleString('en-IN')}</span>
                    {adv.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => handleApprove(adv.id)} className="btn-success btn-sm">
                          <HiOutlineCheck className="w-4 h-4" /> অনুমোদন
                        </button>
                        <button onClick={() => handleReject(adv.id)} className="btn-danger btn-sm">
                          <HiOutlineX className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  {adv.reason && (
                    <p className="text-xs text-ink-600 dark:text-ink-400 mt-2 italic">"{adv.reason}"</p>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default Advances;
